import { React, useState, useEffect, useRef } from 'react';
import { PropTypes } from 'prop-types';
import { Modal, Button, Badge, Alert } from 'react-bootstrap';
import CloseButton from 'react-bootstrap/CloseButton';
import { BsXLg } from 'react-icons/bs';
import Select from 'react-select';

import styles from './CreateStudentGroupModal.module.css';
import { TLPBackend, toMasterTeacherId } from '../../common/utils';
import { SCHOOL_YEARS } from '../../common/config';

import StudentGroupDropdown from '../EditStudentGroupModal/StudentGroupDropdown';

const CreateStudentGroupModal = ({ siteId, teacherId, isOpen, setIsOpen, onCreated }) => {
  const schoolYears = SCHOOL_YEARS;
  const schoolCycles = ['Cycle 1', 'Cycle 2', 'Cycle 3', 'Cycle 4'];
  const meetingDays = [
    'Mondays',
    'Tuesdays',
    'Wednesdays',
    'Thursdays',
    'Fridays',
    'Saturdays',
    'Sundays',
  ];

  const [studentGroupInfo, setStudentGroupInfo] = useState({
    groupName: '',
    masterTeacherId: teacherId,
    schoolYear: 'Year',
    schoolCycle: 'Cycle',
    meetingDay: 'Day',
    meetingTime: 'Time',
  });

  const [validName, setValidName] = useState(true);
  const [validYear, setValidYear] = useState(true);
  const [validCycle, setValidCycle] = useState(true);
  const [validDay, setValidDay] = useState(true);
  const [validTime, setValidTime] = useState(true);

  // Students in the group
  const [currentStudents, setCurrentStudents] = useState([]);

  // Students in the system that are not in the group
  const [possibleStudents, setPossibleStudents] = useState();
  const [possibleStudentsLoaded, setPossibleStudentsLoaded] = useState(false);

  const [showAlert, setShowAlert] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [groupConfirmed, setGroupConfirmed] = useState(false);
  const [saveUnconfirmed, setSaveUnconfirmed] = useState(false);
  const savingRef = useRef(false);
  const createdGroupId = useRef(null);

  const getPossibleStudents = async () => {
    const systemStudents = await TLPBackend.get(`/students`, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    try {
      const filteredStudents = await systemStudents.data.filter(
        // Possible students include students who are not assigned to a student group
        studentObj =>
          studentObj.studentGroupId === null &&
          !Object.keys(currentStudents).some(
            s => currentStudents[s].studentId === studentObj.studentId,
          ),
      );
      // Map student objects to objects w/ only ID, first name, last name
      const possibleStudentsObj = Object.assign(
        {},
        ...filteredStudents.map(student => ({
          [student.studentId]: {
            studentId: student.studentId,
            firstName: student.firstName,
            lastName: student.lastName,
          },
        })),
      );
      setPossibleStudents(possibleStudentsObj);
      setPossibleStudentsLoaded(true);
    } catch (err) {
      // setError(err);
    }
  };

  const addToCurrentStudents = studentId => {
    if (savingRef.current || groupConfirmed || saveUnconfirmed) return;
    // If studentId is already in currentStudents, skip
    if (studentId in currentStudents) {
      return;
    }

    setCurrentStudents({ ...currentStudents, [studentId]: possibleStudents[studentId] });
    // Remove student from possibleStudents
    const newPossibleStudents = possibleStudents;
    delete newPossibleStudents[studentId];
    setPossibleStudents(newPossibleStudents);
  };

  const removeFromCurrentStudents = studentId => {
    if (savingRef.current || groupConfirmed || saveUnconfirmed) return;
    // If studentId is already in possibleStudents, skip
    if (studentId === -1 || studentId in possibleStudents) {
      return;
    }

    setPossibleStudents({ ...possibleStudents, [studentId]: currentStudents[studentId] });
    // Remove student from possibleStudents
    const newCurrentStudents = currentStudents;
    delete newCurrentStudents[studentId];
    setCurrentStudents(newCurrentStudents);
  };

  // Check that fields are not the default
  const checkNotDefaultFields = () => {
    let notDefault = true;
    if (studentGroupInfo.groupName === '') {
      setValidName(false);
      notDefault = false;
    }
    if (studentGroupInfo.schoolYear === 'Year') {
      setValidYear(false);
      notDefault = false;
    }
    if (studentGroupInfo.schoolCycle === 'Cycle') {
      setValidCycle(false);
      notDefault = false;
    }
    if (studentGroupInfo.meetingDay === 'Day') {
      setValidDay(false);
      notDefault = false;
    }
    if (studentGroupInfo.meetingTime === 'Time') {
      setValidTime(false);
      notDefault = false;
    }
    return notDefault;
  };

  const closeModal = () => setIsOpen(false);

  const resetModal = async () => {
    if (savingRef.current) return;
    if (saveUnconfirmed) {
      closeModal();
      return;
    }
    if (createdGroupId.current !== null) {
      try {
        await onCreated();
      } catch (err) {
        setSaveError(
          'The group was created. Refresh the page to load it before starting another group.',
        );
        return;
      }
    }
    createdGroupId.current = null;
    setGroupConfirmed(false);
    setSaveError('');
    setStudentGroupInfo({
      groupName: '',
      masterTeacherId: teacherId,
      schoolYear: 'Year',
      schoolCycle: 'Cycle',
      meetingDay: 'Day',
      meetingTime: 'Time',
    });
    setValidName(true);
    setValidYear(true);
    setValidCycle(true);
    setValidDay(true);
    setValidTime(true);
    setCurrentStudents([]);
    closeModal();
  };

  const createStudentGroup = async () => {
    if (savingRef.current || saveUnconfirmed || !checkNotDefaultFields()) {
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setSaveError('');
    try {
      if (createdGroupId.current === null) {
        const studentGroupData = await TLPBackend.post(
          `/student-groups`,
          {
            name: studentGroupInfo.groupName,
            year: Number(studentGroupInfo.schoolYear.slice(0, 4)),
            cycle: studentGroupInfo.schoolCycle.slice(6),
            // null when there is no teacher yet -- the group still gets created, and
            // assigning a teacher to the site later stamps it onto the group
            masterTeacherId: toMasterTeacherId(studentGroupInfo.masterTeacherId),
            siteId,
            meetingDay: studentGroupInfo.meetingDay.slice(0, -1),
            meetingTime: studentGroupInfo.meetingTime,
          },
          { timeout: 30000 },
        );
        createdGroupId.current = studentGroupData.data.groupId;
        setGroupConfirmed(true);
      }
      // Array of studentIds of students added to created group
      const addedStudents = Object.keys(currentStudents).map(stringId => {
        return Number(stringId);
      });

      await TLPBackend.put(
        `/students/update-bulk`,
        {
          studentIds: addedStudents,
          studentGroupId: createdGroupId.current,
        },
        { timeout: 30000 },
      );
      await onCreated();
      savingRef.current = false;
      createdGroupId.current = null;
      setGroupConfirmed(false);
      resetModal();
      closeModal();
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 5000);
    } catch (err) {
      if (createdGroupId.current === null) setSaveUnconfirmed(true);
      setSaveError(
        createdGroupId.current === null
          ? 'Could not confirm the save. Refresh the page to check for the group before trying again.'
          : 'The group was created, but the remaining update failed. Save again to finish the existing group.',
      );
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  useEffect(async () => {
    if (!isOpen) {
      return;
    }
    await getPossibleStudents();
  }, [isOpen]);

  const StudentBadges = () => (
    <>
      {Object.keys(currentStudents).map(studentId => {
        const studentObj = currentStudents[studentId];
        return (
          <Badge bg="secondary" className={styles['student-badge']} id={studentId} key={studentId}>
            <BsXLg
              className={styles['student-badge-icon']}
              cursor="pointer"
              id={studentId}
              onClick={() => removeFromCurrentStudents(studentId)}
            />
            {`${studentObj.firstName} ${studentObj.lastName}`}
          </Badge>
        );
      })}
    </>
  );

  const updateName = name => {
    const { value } = name.target;
    setStudentGroupInfo(current => ({ ...current, groupName: value }));
    setValidName(true);
  };

  // Styling for react-select component used to search for students
  const selectStyles = {
    option: (provided, state) => ({
      ...provided,
      background: state.isFocused ? 'whitesmoke' : 'white',
    }),
    indicatorSeparator: provided => ({
      ...provided,
      background: 'white',
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      background: 'white',
      color: state.isFocused ? 'white' : 'white',
    }),
  };

  return (
    <div>
      <Modal
        show={isOpen}
        onHide={resetModal}
        aria-labelledby="create-group-title"
        dialogClassName={styles['modal-content']}
      >
        <Modal.Header dialogClassName={styles['create-student-group-modal-header']}>
          <Modal.Title
            id="create-group-title"
            dialogClassName={styles['create-student-group-modal-top-bar-title']}
          >
            Create Student Group
          </Modal.Title>
          <CloseButton onClick={() => resetModal()} />
        </Modal.Header>
        <Modal.Body>
          {saveError && <Alert variant="danger">{saveError}</Alert>}
          <fieldset
            disabled={saving || groupConfirmed || saveUnconfirmed}
            className={styles['create-student-group-modal-body']}
          >
            <div className={styles['create-student-group-modal-field-desc']}>Group Name</div>
            <input
              className={validName ? styles['modal-text-input'] : styles['modal-text-input-error']}
              type="text"
              aria-label="Group Name"
              value={studentGroupInfo.groupName}
              onChange={updateName}
              placeholder="Group"
            />
            <div className={styles['meeting-day-err-message']}>
              {validName ? '' : 'Invalid Group Name'}
            </div>
            <div className={styles['create-student-group-modal-field-desc']}>School Cycle</div>
            <div className={styles['create-student-group-school-cycle']}>
              <StudentGroupDropdown
                choices={schoolYears}
                current={studentGroupInfo.schoolYear}
                setFn={eventKey => {
                  setStudentGroupInfo({
                    ...studentGroupInfo,
                    schoolYear: eventKey,
                  });
                  setValidYear(true);
                }}
                errorState={validYear}
              />
              <StudentGroupDropdown
                choices={schoolCycles}
                current={studentGroupInfo.schoolCycle}
                setFn={eventKey => {
                  setStudentGroupInfo({
                    ...studentGroupInfo,
                    schoolCycle: eventKey,
                  });
                  setValidCycle(true);
                }}
                errorState={validCycle}
              />
              <div className={styles['school-year-err-message']}>
                {validYear ? '' : 'Invalid School Year'}
              </div>
              <div className={styles['school-cycle-err-message']}>
                {validCycle ? '' : 'Invalid School Cycle'}
              </div>
            </div>
            <div className={styles['create-student-group-modal-field-desc']}>Meeting Time</div>
            <div className={styles['create-student-group-meeting']}>
              <StudentGroupDropdown
                choices={meetingDays}
                current={studentGroupInfo.meetingDay}
                setFn={eventKey => {
                  setStudentGroupInfo({
                    ...studentGroupInfo,
                    meetingDay: eventKey,
                  });
                  setValidDay(true);
                }}
                errorState={validDay}
              />
              <input
                className={validTime ? styles['time-input'] : styles['time-input-error']}
                type="time"
                aria-label="Meeting time"
                min="00:00"
                max="23:59"
                value={studentGroupInfo.meetingTime}
                onChange={event => {
                  setStudentGroupInfo({
                    ...studentGroupInfo,
                    meetingTime: event.target.value,
                  });
                  setValidTime(true);
                }}
              />
              <div className={styles['meeting-day-err-message']}>
                {validDay ? '' : 'Invalid Meeting Day'}
              </div>
              <div className={styles['meeting-time-err-message']}>
                {validTime ? '' : 'Invalid Meeting Time'}
              </div>
            </div>
            <div className={styles['create-student-group-modal-field-desc']}>
              Add/Remove Students
            </div>
            <div className={styles['create-student-group-badges']}>
              <StudentBadges />
            </div>
            <div className={styles['students-select']}>
              {possibleStudentsLoaded ? (
                <Select
                  options={Object.keys(possibleStudents).map(studentId => ({
                    value: studentId,
                    label: `${possibleStudents[studentId].firstName} ${possibleStudents[studentId].lastName}`,
                  }))}
                  onChange={opt => addToCurrentStudents(opt.value)}
                  placeholder="Select Students"
                  aria-label="Add students"
                  isDisabled={saving || groupConfirmed || saveUnconfirmed}
                  styles={selectStyles}
                />
              ) : null}
            </div>
          </fieldset>
        </Modal.Body>
        <Modal.Footer>
          <div className={styles['create-student-group-modal-footer']}>
            <div className={styles['create-student-group-cancel-button']}>
              <Button variant="secondary" disabled={saving} onClick={() => resetModal()}>
                Cancel
              </Button>
            </div>
            <div className={styles['create-student-group-save-button']}>
              <Button
                variant="primary"
                disabled={saving || saveUnconfirmed}
                onClick={() => createStudentGroup()}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
      {showAlert ? (
        <div className="center-block">
          <Alert variant="success" className={styles['alert-custom']}>
            Student Group successfully created.
            <CloseButton className="alert-close-btn" onClick={() => setShowAlert(false)} />
          </Alert>
        </div>
      ) : null}
    </div>
  );
};

CreateStudentGroupModal.defaultProps = {
  siteId: null,
  // a group can be set up for a site that has not been staffed yet
  teacherId: null,
  onCreated: () => {},
};

CreateStudentGroupModal.propTypes = {
  siteId: PropTypes.number,
  teacherId: PropTypes.number,
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
};

export default CreateStudentGroupModal;
